import { createSlice } from '@reduxjs/toolkit';
import { ACTIVITY_FILTERS } from './constants';

function dropChatNotifications(groups) {
  return groups
    .map((g) => ({ ...g, events: g.events.filter((e) => e.notificationType !== 'ChatNotification') }))
    .filter((g) => g.events.length > 0);
}

export function mergeGroups(existing, incoming) {
  const result = existing.map((g) => ({ ...g, events: [...g.events] }));
  for (const newGroup of incoming) {
    const idx = result.findIndex((g) => g.taskId === newGroup.taskId);
    if (idx !== -1) {
      // Prefer the title that already has "Thread in" prefix (set by server)
      if (newGroup.taskTitle?.startsWith('Thread in ') || !result[idx].taskTitle) {
        result[idx].taskTitle = newGroup.taskTitle;
      }
      if (newGroup.isChatThread) result[idx].isChatThread = true;
      const allEvents = [...result[idx].events, ...newGroup.events];
      const seen = new Map(allEvents.map((e) => [e._id?.toString(), e]));
      const deduped = Array.from(seen.values());
      // Mirror server dedup: latest per type wins; for TaskNotification, latest per action wins
      const OVERWRITE_BY_TYPE = new Set(['CommentNotification', 'ChatReplyNotification']);
      const latestByKey = new Map();
      const accumulated = [];
      for (const e of deduped) {
        if (OVERWRITE_BY_TYPE.has(e.notificationType)) {
          const key = e.notificationType;
          const prev = latestByKey.get(key);
          if (!prev || new Date(e.createdAt) > new Date(prev.createdAt)) {
            latestByKey.set(key, e);
          }
        } else if (e.notificationType === 'TaskNotification') {
          const key = `TaskNotification:${e.action ?? ''}`;
          const prev = latestByKey.get(key);
          if (!prev || new Date(e.createdAt) > new Date(prev.createdAt)) {
            latestByKey.set(key, e);
          }
        } else {
          accumulated.push(e);
        }
      }
      result[idx].events = [...accumulated, ...latestByKey.values()]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (newGroup.latestAt > result[idx].latestAt) {
        result[idx].latestAt = newGroup.latestAt;
      }
    } else {
      result.push({ ...newGroup });
    }
  }
  return result;
}

export function groupByTask(notifications) {
  const map = new Map();
  for (const n of notifications) {
    const isChatThread = n.notificationType === 'ChatReplyNotification';

    // Mirror server-side grouping: chat notifications key by chatRoom, others by task
    const taskId = isChatThread
      ? (n.chatRoom?._id?.toString() ?? n.chatRoom?.toString() ?? n._id.toString())
      : (n.task?._id?.toString() ?? n._id.toString());

    if (!map.has(taskId)) {
      // Mirror server logic: chatRoom.roomName → assignor.fullName → task title
      const chatRoomName = isChatThread
        ? (n.chatRoom?.roomName ?? n.chatRoom?.roomTitle ?? n.assignor?.fullName ?? null)
        : null;
      map.set(taskId, {
        taskId,
        taskTitle: chatRoomName ? `Thread in ${chatRoomName}` : (n.task?.taskTitle ?? null),
        projectId: n.task?.projectId,
        titleIdentifier: isChatThread ? null : n.task?.titleIdentifier,
        isChatThread,
        latestAt: n.createdAt,
        events: [],
      });
    }
    const group = map.get(taskId);
    group.events.push(n);
    if (new Date(n.createdAt) > new Date(group.latestAt)) {
      group.latestAt = n.createdAt;
    }
  }
  return Array.from(map.values());
}

const initialState = {
  unreadGroups: [],
  readGroups: [],
  nextCursor: null,
  hasMore: false,
  hasMoreUnread: false,
  unreadTaskCount: 0,
  pendingGroups: [],
  pendingCount: 0,
  activeFilter: ACTIVITY_FILTERS[0],
  loading: false,
  loadingMore: false,
  error: null,
  lastSeenTimestamp: null,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    fetchNotificationsStart(state, action) {
      state.loading = true;
      state.error = null;
      state.activeFilter = action.payload?.filter ?? state.activeFilter;
      state.unreadGroups = [];
      state.readGroups = [];
      state.nextCursor = null;
      state.hasMore = false;
      state.pendingGroups = [];
      state.pendingCount = 0;
    },
    fetchNotificationsSuccess(state, action) {
      const { unreadGroups, readGroups, nextCursor, hasMore, hasMoreUnread, unreadTaskCount, serverTime } =
        action.payload;
      state.loading = false;
      state.unreadGroups = dropChatNotifications(unreadGroups ?? []);
      state.readGroups = dropChatNotifications(readGroups ?? []);
      state.nextCursor = nextCursor ?? null;
      state.hasMore = hasMore ?? false;
      state.hasMoreUnread = hasMoreUnread ?? false;
      // Only update the global badge count when not filtering — filtered responses return a subset
      if (state.activeFilter === ACTIVITY_FILTERS[0]) {
        state.unreadTaskCount = unreadTaskCount ?? 0;
      }
      state.lastSeenTimestamp = serverTime ?? null;
    },
    fetchNotificationsFailure(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    loadMoreStart(state) {
      state.loadingMore = true;
      state.error = null;
    },
    loadMoreSuccess(state, action) {
      const { readGroups, nextCursor, hasMore, serverTime } = action.payload;
      state.loadingMore = false;
      state.readGroups = mergeGroups(state.readGroups, dropChatNotifications(readGroups ?? []));
      state.nextCursor = nextCursor ?? null;
      state.hasMore = hasMore ?? false;
      state.lastSeenTimestamp = serverTime ?? null;
    },
    loadMoreFailure(state, action) {
      state.loadingMore = false;
      state.error = action.payload;
    },
    socketNotificationReceived(state, action) {
      const notification = action.payload?.notification;
      if (!notification) return;
      if (notification.notificationType === 'ChatNotification') return;

      const filterMap = {
        [ACTIVITY_FILTERS[1]]: (n) => n.notificationType === 'TaskNotification',
        [ACTIVITY_FILTERS[2]]: (n) => !!n.assignee?.isMentioned,
        [ACTIVITY_FILTERS[3]]: (n) => n.notificationType === 'ChatReplyNotification',
      };
      const filterFn = filterMap[state.activeFilter];
      if (filterFn && !filterFn(notification)) return;

      const notifId = notification._id?.toString();
      const alreadyInPending = state.pendingGroups.some((g) =>
        g.events.some((e) => e._id?.toString() === notifId),
      );
      if (alreadyInPending) return;

      const isChatThread = notification.notificationType === 'ChatReplyNotification';
      const taskId = isChatThread
        ? (notification.chatRoom?._id?.toString() ?? notification.chatRoom?.toString() ?? notification._id?.toString())
        : (notification.task?._id?.toString() ?? notification._id?.toString());
      const isNewTask =
        !state.unreadGroups.some((g) => g.taskId === taskId) &&
        !state.pendingGroups.some((g) => g.taskId === taskId);

      const newGroups = groupByTask([notification]);
      state.pendingGroups = mergeGroups(state.pendingGroups, newGroups);
      // Recalculate from actual groups so overwritten same-type events don't inflate the count
      state.pendingCount = state.pendingGroups.length;

      if (isNewTask) {
        state.unreadTaskCount += 1;
      }
    },
    applyPendingNotifications(state) {
      if (state.pendingCount === 0) return;
      state.unreadGroups = mergeGroups(state.pendingGroups, state.unreadGroups);
      state.pendingGroups = [];
      state.pendingCount = 0;
    },
    markGroupRead(state, action) {
      const { taskId } = action.payload;
      const idx = state.unreadGroups.findIndex((g) => g.taskId === taskId);
      if (idx === -1) return;
      const [group] = state.unreadGroups.splice(idx, 1);
      group.events = group.events.map((e) => ({
        ...e,
        assignee: { ...e.assignee, isViewed: true },
      }));
      state.readGroups = mergeGroups([group], state.readGroups);
      state.unreadTaskCount = Math.max(0, state.unreadTaskCount - 1);
    },
    markAllRead(state) {
      const viewedGroups = state.unreadGroups.map((g) => ({
        ...g,
        events: g.events.map((e) => ({
          ...e,
          assignee: { ...e.assignee, isViewed: true },
        })),
      }));
      state.readGroups = mergeGroups(viewedGroups, state.readGroups);
      state.unreadGroups = [];
      state.unreadTaskCount = 0;
    },
    setUnreadTaskCount(state, action) {
      state.unreadTaskCount = action.payload ?? 0;
    },
    setActiveFilter(state, action) {
      state.activeFilter = action.payload;
    },
    addReconnectPendingGroups(state, action) {
      const { groups } = action.payload;
      if (!groups?.length) return;
      state.pendingGroups = mergeGroups(groups, state.pendingGroups);
      state.pendingCount = state.pendingGroups.length;
    },
  },
});

export const {
  fetchNotificationsStart,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  loadMoreStart,
  loadMoreSuccess,
  loadMoreFailure,
  socketNotificationReceived,
  applyPendingNotifications,
  markGroupRead,
  markAllRead,
  setUnreadTaskCount,
  setActiveFilter,
  addReconnectPendingGroups,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
